import { useCallback, useState } from 'react'
import { Box, Typography, Stack, Button, TextField, Divider, Drawer, Autocomplete, IconButton } from '@mui/material'
import PropTypes from 'prop-types'
import { Close } from '@mui/icons-material'
import { dateFns, EXPORT_HISTORY_STATUS } from 'constants/index'
import { useExportHistoryFilters } from 'hooks/useExportHistoryFilters'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { ptBR } from 'date-fns/locale'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { getDateWithClientTimezone } from 'utils/date'

function FilterDrawer({ open, toggleDrawer }) {
  const [errors, setErrors] = useState({
    startExportPeriod: false,
    endExportPeriod: false,
  })
  const [openPicker, setOpenPicker] = useState({
    startExportPeriod: false,
    endExportPeriod: false,
  })
  const { filters, setFilters, unsetFilters } = useExportHistoryFilters()
  const [rangeDate, setRangeDate] = useState(() => {
    if (!filters.startExportPeriod) {
      return {
        start: null,
        end: null,
      }
    }
    if (filters.startExportPeriod && typeof filters.startExportPeriod === 'string') {
      const start = getDateWithClientTimezone(
        dateFns.startOfDay(new Date(filters.startExportPeriod)),
      ).clientWithTimezone
      const end = getDateWithClientTimezone(dateFns.endOfDay(new Date(filters.endExportPeriod))).clientWithTimezone
      return {
        start,
        end,
      }
    }
    return {
      start: filters.startExportPeriod,
      end: filters.endExportPeriod,
    }
  })

  const [selectedStatus, setSelectedStatus] = useState(() => {
    if (filters.status && filters.status?.length) {
      /**
       * exit of Array.filter => Array.includes() with O(n^2)
       * to Array.filter => Set.prototype.has() with O(n)
       */
      return EXPORT_HISTORY_STATUS.filter((item) => new Set(filters.status).has(item.id.toString()))
    }
    return []
  })

  const handleStatusChange = useCallback((_, newValue) => {
    setSelectedStatus(newValue)
  }, [])

  const handleChangeDate = useCallback((label, value) => {
    if (label === 'startExportPeriod') {
      setRangeDate((oldState) => ({
        ...oldState,
        start: value,
      }))
    } else {
      setRangeDate((oldState) => ({
        ...oldState,
        end: value,
      }))
    }
  }, [])

  const handleClearSelectedFilters = useCallback(() => {
    setSelectedStatus([])
    setRangeDate({
      start: null,
      end: null,
    })
    unsetFilters()
  }, [unsetFilters])

  const handleApplyFilter = useCallback(() => {
    const filterParams = filters

    if (selectedStatus?.length) {
      Object.assign(filterParams, { status: selectedStatus.map((item) => item.id) })
    } else {
      Object.assign(filterParams, { status: [] })
    }

    if (rangeDate.start && rangeDate.end) {
      Object.assign(filterParams, {
        startExportPeriod: getDateWithClientTimezone(dateFns.startOfDay(new Date(rangeDate.start))).clientWithTimezone,
        endExportPeriod: getDateWithClientTimezone(dateFns.endOfDay(new Date(rangeDate.end))).clientWithTimezone,
      })
    } else {
      Object.assign(filterParams, {
        startExportPeriod: null,
        endExportPeriod: null,
      })
    }

    setFilters(filterParams)
    toggleDrawer()
  }, [filters, rangeDate, selectedStatus, setFilters, toggleDrawer])

  const disabledApplyButton =
    (!selectedStatus?.length && !rangeDate.end) || errors.startExportPeriod || errors.endExportPeriod

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={toggleDrawer}
      data-testid="swiapeable-drawer"
      sx={{
        '& .HiTheme-Drawer-paper': {
          padding: '1.5rem',
          width: '22rem',
        },
      }}
      ModalProps={{
        disableAutoFocus: true,
        disableEnforceFocus: true,
      }}
    >
      <Stack flexDirection="column" gap={2}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="body" color="#666" fontWeight="700" fontSize="2rem" padding="0 12px">
            Filtros
          </Typography>
          <IconButton onClick={toggleDrawer} data-testid="close-drawer">
            <Close />
          </IconButton>
        </Box>
        <Autocomplete
          disablePortal
          data-testid="status-field-autocomplete"
          fullWidth
          multiple
          limitTags={1}
          id="ad-filter"
          options={EXPORT_HISTORY_STATUS}
          getOptionLabel={(option) => option.label}
          disableCloseOnSelect
          value={selectedStatus}
          ChipProps={{
            'data-id': 'chip-filter',
          }}
          renderInput={(params) => (
            <TextField
              style={{ color: '#00aeef' }}
              {...params}
              variant="outlined"
              data-id="form-field-status"
              label="Status"
              placeholder="Selecionar status"
            />
          )}
          noOptionsText="Nenhum resultado"
          onChange={handleStatusChange}
        />

        <Stack>
          <Typography variant="caption" fontSize="1rem" color="#666" fontWeight={700}>
            Período exportado
          </Typography>

          <Stack container direction="column" justifyContent="space-between" gap="1rem" style={{ marginTop: 18 }}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
              <DatePicker
                InputAdornmentProps={{
                  'data-id': 'openPickerAdornment-startPeriod',
                  style: { marginLeft: '-12px' },
                }}
                InputProps={{
                  'data-id': 'start-period',
                  'data-testid': 'input-start-data-testid',
                }}
                PopperProps={{
                  'data-id': 'start-datePicker',
                  'data-testid': 'datepicker-start-data-testid',
                  autoFocus: false,
                }}
                PaperProps={{
                  autoFocus: false,
                }}
                DialogProps={{
                  disableAutoFocus: true,
                  disableAutoFocusItem: true,
                  autoFocus: false,
                  disableEnforceFocus: true,
                }}
                label="De"
                inputFormat="dd/MM/yyyy"
                disableFuture
                disableOpenPicker
                value={rangeDate.start}
                open={openPicker.startExportPeriod}
                onClose={() =>
                  setOpenPicker((oldState) => ({ ...oldState, startExportPeriod: !oldState.startExportPeriod }))
                }
                onChange={(data) => handleChangeDate('startExportPeriod', data)}
                renderInput={(params) => (
                  <TextField
                    sx={{ width: '100%' }}
                    {...params}
                    onClick={() =>
                      setOpenPicker((oldState) => ({ ...oldState, startExportPeriod: !oldState.startExportPeriod }))
                    }
                    error={errors.startExportPeriod}
                    helperText={errors?.startExportPeriod ? 'Valor informado é invalido' : null}
                  />
                )}
                onError={(err) => setErrors((oldState) => ({ ...oldState, startExportPeriod: !!err }))}
              />

              <DatePicker
                InputAdornmentProps={{
                  'data-id': 'openPickerAdornment-endPeriod',
                  style: { marginLeft: '-12px' },
                }}
                InputProps={{
                  'data-id': 'end-period',
                  'data-testid': 'input-end-data-testid',
                }}
                PopperProps={{
                  'data-id': 'end-datePicker',
                  'data-testid': 'datepicker-end-data-testid',
                  autoFocus: false,
                }}
                PaperProps={{
                  autoFocus: false,
                }}
                DialogProps={{
                  disableAutoFocus: true,
                  disableAutoFocusItem: true,
                  autoFocus: false,
                  disableEnforceFocus: true,
                }}
                label="Até"
                inputFormat="dd/MM/yyyy"
                disableFuture
                disableOpenPicker
                value={rangeDate.end}
                disabled={!rangeDate.start}
                minDate={new Date(rangeDate.start)}
                open={openPicker.endExportPeriod}
                onClose={() =>
                  setOpenPicker((oldState) => ({ ...oldState, endExportPeriod: !oldState.endExportPeriod }))
                }
                onChange={(data) => handleChangeDate('endExportPeriod', data)}
                renderInput={(params) => (
                  <TextField
                    sx={{ width: '100%' }}
                    {...params}
                    onClick={() =>
                      setOpenPicker((oldState) => ({ ...oldState, endExportPeriod: !oldState.endExportPeriod }))
                    }
                    error={errors.endExportPeriod}
                    helperText={errors?.endExportPeriod ? 'Valor informado é invalido' : null}
                  />
                )}
                onError={(err) => setErrors((oldState) => ({ ...oldState, endExportPeriod: !!err }))}
              />
            </LocalizationProvider>
          </Stack>
        </Stack>

        <Divider sx={{ margin: '30px -24px 10px -24px', backgroundColor: '#cacaca' }} />
        <Box display="flex" justifyContent="flex-end" gap={2}>
          <Button
            variant="text"
            sx={{ color: '#EF3E39' }}
            onClick={() => {
              handleClearSelectedFilters()
            }}
            data-testid="reset-btn-action"
          >
            Redefinir
          </Button>
          <Button
            variant="contained"
            sx={{
              backgroundColor: '#00aeef',
              '&:hover': {
                backgroundColor: '#009deb',
              },
            }}
            disableElevation
            disabled={disabledApplyButton}
            onClick={() => {
              handleApplyFilter()
            }}
            data-testid="apply-btn-action"
          >
            Aplicar
          </Button>
        </Box>
      </Stack>
    </Drawer>
  )
}

FilterDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  toggleDrawer: PropTypes.func.isRequired,
}

export default FilterDrawer
